import React, { useState, useMemo, useEffect } from 'react';
import { Text, View, TouchableOpacity } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import InputSection from '@/components/General/inputs/InputSection';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import SelectInput from '@/components/General/inputs/SelectInput';
import Checkbox from '@/components/General/inputs/Checkbox';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { createBlackoutPeriod, updateBlackoutPeriod } from '@/data/events';
import Toast from 'react-native-toast-message';
import { useRouter, useLocalSearchParams } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import { StyleSheet } from 'react-native-unistyles';
import SectionTitle from '@/components/General/SectionTitle';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

export default function AddBlackoutPeriodModal() {
    const {selectedDate, blackoutID, startDate: startDateParam, endDate: endDateParam} = useLocalSearchParams();
    const isEditing = !!blackoutID;

    const toMidnight = (dateInput) => {
        const d = new Date(dateInput);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const toEndOfDay = (dateInput) => {
        const d = new Date(dateInput);
        d.setHours(23, 59, 0, 0);
        return d;
    };

    const parseInitialFullDay = () => {
        if (startDateParam && endDateParam) {
            const s = new Date(startDateParam);
            const e = new Date(endDateParam);
            return s.getHours() === 0 && s.getMinutes() === 0 && e.getHours() === 23 && e.getMinutes() === 59;
        }
        return true;
    };

    const [startDate, setStartDate] = useState(
        startDateParam
            ? new Date(startDateParam)
            : selectedDate
                ? toMidnight(selectedDate)
                : toMidnight(new Date())
    );
    const [endDate, setEndDate] = useState(
        endDateParam
            ? new Date(endDateParam)
            : selectedDate
                ? toEndOfDay(selectedDate)
                : undefined
    );
    const [isFullDay, setIsFullDay] = useState(parseInitialFullDay());
    const [selectedPersonIDs, setSelectedPersonIDs] = useState([]);

    useEffect(() => {
        if (isFullDay) {
            if (startDate) setStartDate(toMidnight(startDate));
            if (endDate) setEndDate(toEndOfDay(endDate));
        }
    }, [isFullDay]);

    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const router = useRouter();

    const familyOptions = useMemo(() => {
        const members = loggedInUser?.Family?.FamilyMembers || [];
        const allPeople = members.some(m => m.id === loggedInUser?.id)
            ? members
            : [{ id: loggedInUser?.id, firstName: loggedInUser?.firstName, lastName: loggedInUser?.lastName }, ...members];
        return allPeople.map(p => ({
            label: `${p.firstName} ${p.lastName}`,
            value: p.id
        }));
    }, [loggedInUser]);

    useEffect(() => {
        if (loggedInUser?.id && selectedPersonIDs.length === 0) {
            setSelectedPersonIDs([loggedInUser.id]);
        }
    }, [loggedInUser?.id]);

    const createBlackoutPeriodMutation = useMutation({
        mutationFn: createBlackoutPeriod,
        onSuccess: () => {
            Toast.show({
                type: 'success',
                text2: 'Blackout Period has been added successfully!'
            })

            queryClient.invalidateQueries({queryKey: QueryKeys.blackoutDates.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.eventInstances.all()})
            router.back();
        }
    })

    const updateBlackoutPeriodMutation = useMutation({
        mutationFn: updateBlackoutPeriod,
        onSuccess: () => {
            Toast.show({
                type: 'success',
                text2: 'Blackout Period has been updated successfully!'
            })

            queryClient.invalidateQueries({queryKey: QueryKeys.blackoutDates.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.eventInstances.all()})
            router.back();
        }
    })

    const calcEndDate = (startDate, add_hours) => {
        if(!startDate){
            return false;
        }

        let endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + parseInt(add_hours));
        return endDate;
    }

    const addDays = (dateInput, days) => {
        const d = new Date(dateInput);
        d.setDate(d.getDate() + days);
        return d;
    };

    const applyPreset = (hours, days) => {
        if (!startDate) return;
        if (isFullDay && days !== undefined) {
            setEndDate(toEndOfDay(addDays(startDate, days)));
        } else {
            setEndDate(calcEndDate(startDate, hours));
        }
    };

    return (
        <ModalWrapper
                title={isEditing ? "Edit Blackout Period" : "Add Blackout Period"}
                onConfirm={() => {
                    if(!startDate || !endDate){
                        Toast.show({
                            type: 'error',
                            text2: 'Please select a start and end date'
                        })
                        return;
                    }

                    if (!isEditing && selectedPersonIDs.length === 0) {
                        Toast.show({
                            type: 'error',
                            text2: 'Please select at least one person'
                        })
                        return;
                    }

                    if (isEditing) {
                        updateBlackoutPeriodMutation.mutate({id: blackoutID, startDate, endDate})
                    } else {
                        createBlackoutPeriodMutation.mutate({personIDs: selectedPersonIDs, startDate, endDate})
                    }
                }}
        >
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>

                <InputSection title="Dates to Black Out" description="A Blackout Period is a time range that you are unavailable to be assigned tasks. This could be a partial day, or span multiple days." >
                    <Checkbox
                        label="Partial Days"
                        helpText="Use Partial Days to include time ranges if you are only unavailable for part of the day on your start or end date."
                        checked={!isFullDay}
                        onChange={(checked) => setIsFullDay(!checked)}
                        size={20}
                    />
                    
                    <DatePickerInput
                        title="Start Date"
                        minimumDate={new Date()}
                        mode={isFullDay ? "date" : "datetime"}
                        value={startDate}
                        onUpdate={(new_value) => {
                            const normalized = isFullDay ? toMidnight(new_value) : new_value;
                            setStartDate(normalized);
                            setEndDate(endDate < normalized || !endDate ? (isFullDay ? toEndOfDay(normalized) : calcEndDate(normalized, 24)) : endDate)
                        }}
                    />

                    <DatePickerInput
                        title="End Date"
                        minimumDate={startDate ? new Date(startDate) : undefined}
                        mode={isFullDay ? "date" : "datetime"}
                        value={endDate}
                        onUpdate={(new_value) => {
                            const normalized = isFullDay ? toEndOfDay(new_value) : new_value;
                            setEndDate(normalized);
                        }}
                    />

                    <View>
                        <SectionTitle title="Preset Durations" />

                        <View style={styles.durationContainer}>
                            <TouchableOpacity style={styles.durationButton} onPress={() => applyPreset(8)}>
                                <Text style={styles.durationButtonText}>+8 Hours</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={styles.durationButton} onPress={() => applyPreset(24, 1)}>
                                <Text style={styles.durationButtonText}>+1 Day</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={styles.durationButton} onPress={() => applyPreset(48, 2)}>
                                <Text style={styles.durationButtonText}>+2 Days</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={styles.durationButton} onPress={() => applyPreset(168, 7)}>
                                <Text style={styles.durationButtonText}>+1 Week</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={styles.durationButton} onPress={() => applyPreset(336, 14)}>
                                <Text style={styles.durationButtonText}>+2 Weeks</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={styles.durationButton} onPress={() => applyPreset(720, 30)}>
                                <Text style={styles.durationButtonText}>+1 Month</Text>
                            </TouchableOpacity>
                        </View>
                    </View>

                </InputSection>

                {!isEditing && (
                    <InputSection title="Apply To" description="Select which family members this blackout period should apply to.">
                        <SelectInput
                            title="Family Members"
                            placeholder="Select family members"
                            multiple
                            options={familyOptions}
                            value={selectedPersonIDs}
                            onUpdate={setSelectedPersonIDs}
                        />
                    </InputSection>
                )}
            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}


const styles = StyleSheet.create(({colors, fonts, typography}) => ({
    helpText: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        marginTop: 8,
    },
    durationButton: {
        borderRadius:8, 
        paddingVertical:5, 
        paddingHorizontal:8,
        width:80,
        alignItems:"center",
        backgroundColor:colors.border
    },
    durationButtonText: {
        color: colors.text,
        fontSize: 13,
    },
    durationContainer: {
        flexDirection:"row", 
        marginTop: 10, 
        flexWrap:"wrap", 
        alignItems:"center",  
        gap:10
    }
}))